/**
 * Report List View Queries
 *
 * React Query hooks for fetching and managing report list views.
 * Uses mock data with localStorage persistence capability.
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { persistedReportListViews, persistedReportRecords } from '../mockData/reports';
import { getRecordsForObjectPage } from '../recordStorageService';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { USE_RDS_FOR_CORE_OBJECTS, isRdsCoreObject } from '../../config/rdsConfig';
import { normalizeRecordForListView } from '../../utils/listViewRecordNormalizer';
import type { ListView, ListViewData, ListViewRecord } from '../../types/listView';
import type { Report, ReportType, ReportFormat, ReportFrequency, ReportStatus } from '../../types/report';

/**
 * Mock Report Data
 */
const mockReports: Report[] = [
  {
    id: 'report_001',
    name: 'Sales Pipeline Report',
    description: 'Comprehensive view of all opportunities in the sales pipeline',
    objectName: 'Opportunity',
    type: 'Sales',
    format: 'Dashboard',
    frequency: 'Weekly',
    status: 'Active',
    createdBy: 'John Smith',
    createdDate: '2025-01-01T08:00:00Z',
    lastModifiedDate: '2025-01-15T14:30:00Z',
    lastRunDate: '2025-01-15T14:30:00Z',
    nextRunDate: '2025-01-22T08:00:00Z',
    recipients: ['sales@company.com', 'john.smith@company.com'],
    isPublic: true,
    folderId: 'folder_sales',
    folderName: 'Sales Reports'
  },
  {
    id: 'report_002',
    name: 'Customer Support Cases',
    description: 'Active support cases grouped by priority and status',
    objectName: 'Case',
    type: 'Service',
    format: 'Table',
    frequency: 'Daily',
    status: 'Active',
    createdBy: 'Sarah Johnson',
    createdDate: '2024-12-15T10:00:00Z',
    lastModifiedDate: '2025-01-16T09:00:00Z',
    lastRunDate: '2025-01-17T08:00:00Z',
    nextRunDate: '2025-01-18T08:00:00Z',
    recipients: ['support@company.com'],
    isPublic: true,
    folderId: 'folder_service',
    folderName: 'Service Reports'
  },
  {
    id: 'report_003',
    name: 'Marketing Campaign Performance',
    description: 'ROI analysis and conversion metrics for all active campaigns',
    objectName: 'Campaign',
    type: 'Marketing',
    format: 'Chart',
    frequency: 'Monthly',
    status: 'Active',
    createdBy: 'Michael Chen',
    createdDate: '2024-11-20T12:00:00Z',
    lastModifiedDate: '2025-01-10T16:45:00Z',
    lastRunDate: '2025-01-01T00:00:00Z',
    nextRunDate: '2025-02-01T00:00:00Z',
    recipients: ['marketing@company.com', 'michael.chen@company.com'],
    isPublic: true,
    folderId: 'folder_marketing',
    folderName: 'Marketing Reports'
  },
  {
    id: 'report_004',
    name: 'Revenue Forecast',
    description: 'Quarterly revenue projections based on pipeline and closed deals',
    objectName: 'Opportunity',
    type: 'Finance',
    format: 'Dashboard',
    frequency: 'Quarterly',
    status: 'Active',
    createdBy: 'Emily Davis',
    createdDate: '2024-10-01T09:30:00Z',
    lastModifiedDate: '2025-01-05T11:20:00Z',
    lastRunDate: '2025-01-01T00:00:00Z',
    nextRunDate: '2025-04-01T00:00:00Z',
    recipients: ['finance@company.com', 'executives@company.com'],
    isPublic: false,
    folderId: 'folder_finance',
    folderName: 'Finance Reports'
  },
  {
    id: 'report_005',
    name: 'Lead Conversion Analysis',
    description: 'Conversion rates from lead to opportunity by source and industry',
    objectName: 'Lead',
    type: 'Sales',
    format: 'Chart',
    frequency: 'Monthly',
    status: 'Active',
    createdBy: 'John Smith',
    createdDate: '2024-12-01T14:00:00Z',
    lastModifiedDate: '2025-01-12T10:15:00Z',
    lastRunDate: '2025-01-01T00:00:00Z',
    nextRunDate: '2025-02-01T00:00:00Z',
    recipients: ['john.smith@company.com'],
    isPublic: false,
    folderId: 'folder_sales',
    folderName: 'Sales Reports'
  },
  {
    id: 'report_006',
    name: 'Operations Efficiency Dashboard',
    description: 'Key operational metrics including response times and throughput',
    objectName: 'Task',
    type: 'Operations',
    format: 'Dashboard',
    frequency: 'Daily',
    status: 'Active',
    createdBy: 'David Wilson',
    createdDate: '2024-11-15T08:45:00Z',
    lastModifiedDate: '2025-01-16T15:30:00Z',
    lastRunDate: '2025-01-17T08:00:00Z',
    nextRunDate: '2025-01-18T08:00:00Z',
    recipients: ['operations@company.com', 'david.wilson@company.com'],
    isPublic: true,
    folderId: 'folder_operations',
    folderName: 'Operations Reports'
  },
  {
    id: 'report_007',
    name: 'Customer Satisfaction Survey Results',
    description: 'CSAT and NPS scores with trend analysis',
    objectName: 'Case',
    type: 'Service',
    format: 'Summary',
    frequency: 'Monthly',
    status: 'Active',
    createdBy: 'Sarah Johnson',
    createdDate: '2024-09-10T13:20:00Z',
    lastModifiedDate: '2025-01-08T09:45:00Z',
    lastRunDate: '2025-01-01T00:00:00Z',
    nextRunDate: '2025-02-01T00:00:00Z',
    recipients: ['sarah.johnson@company.com', 'executives@company.com'],
    isPublic: true,
    folderId: 'folder_service',
    folderName: 'Service Reports'
  },
  {
    id: 'report_008',
    name: 'Custom Territory Analysis',
    description: 'Sales performance by territory with year-over-year comparison',
    objectName: 'Opportunity',
    type: 'Custom',
    format: 'Table',
    frequency: 'Quarterly',
    status: 'Scheduled',
    createdBy: 'John Smith',
    createdDate: '2024-12-20T10:00:00Z',
    lastModifiedDate: '2025-01-14T16:00:00Z',
    nextRunDate: '2025-04-01T08:00:00Z',
    recipients: ['sales@company.com'],
    isPublic: false,
    folderId: 'folder_custom',
    folderName: 'Custom Reports'
  },
  {
    id: 'report_009',
    name: 'Executive Summary - Q1 2025',
    description: 'High-level overview of all key metrics for executive team',
    objectName: 'Dashboard',
    type: 'Custom',
    format: 'Dashboard',
    frequency: 'One-time',
    status: 'Draft',
    createdBy: 'Emily Davis',
    createdDate: '2025-01-10T11:30:00Z',
    lastModifiedDate: '2025-01-16T14:20:00Z',
    recipients: ['executives@company.com'],
    isPublic: false,
    folderId: 'folder_executive',
    folderName: 'Executive Reports'
  },
  {
    id: 'report_010',
    name: 'Budget vs Actual - 2024',
    description: 'Financial comparison of budgeted vs actual spending by department',
    objectName: 'Budget',
    type: 'Finance',
    format: 'Table',
    frequency: 'One-time',
    status: 'Archived',
    createdBy: 'Emily Davis',
    createdDate: '2024-01-05T09:00:00Z',
    lastModifiedDate: '2024-12-31T23:59:00Z',
    lastRunDate: '2024-12-31T23:59:00Z',
    recipients: ['finance@company.com'],
    isPublic: false,
    folderId: 'folder_finance',
    folderName: 'Finance Reports'
  },
  {
    id: 'report_011',
    name: 'Email Campaign Metrics',
    description: 'Open rates, click-through rates, and conversions for email campaigns',
    objectName: 'Campaign',
    type: 'Marketing',
    format: 'Chart',
    frequency: 'Weekly',
    status: 'Active',
    createdBy: 'Michael Chen',
    createdDate: '2024-12-10T14:30:00Z',
    lastModifiedDate: '2025-01-15T11:00:00Z',
    lastRunDate: '2025-01-15T08:00:00Z',
    nextRunDate: '2025-01-22T08:00:00Z',
    recipients: ['marketing@company.com'],
    isPublic: true,
    folderId: 'folder_marketing',
    folderName: 'Marketing Reports'
  },
  {
    id: 'report_012',
    name: 'Product Performance Analysis',
    description: 'Sales and revenue breakdown by product line',
    objectName: 'Opportunity',
    type: 'Sales',
    format: 'Dashboard',
    frequency: 'Monthly',
    status: 'Active',
    createdBy: 'John Smith',
    createdDate: '2024-11-05T10:15:00Z',
    lastModifiedDate: '2025-01-13T13:45:00Z',
    lastRunDate: '2025-01-01T00:00:00Z',
    nextRunDate: '2025-02-01T00:00:00Z',
    recipients: ['john.smith@company.com', 'sales@company.com'],
    isPublic: true,
    folderId: 'folder_sales',
    folderName: 'Sales Reports'
  }
];

/**
 * Mock Report List Views
 */
const mockReportListViews: ListView[] = [
  {
    id: 'lv_reports_all',
    name: 'All Reports',
    apiName: 'all_reports',
    description: 'All reports in the system',
    objectApiName: 'Report',
    isDefault: true,
    columns: [
      {
        fieldApiName: 'name',
        fieldLabel: 'Report Name',
        fieldType: 'text',
        isVisible: true,
        order: 0
      },
      {
        fieldApiName: 'objectName',
        fieldLabel: 'Object',
        fieldType: 'text',
        isVisible: true,
        order: 1
      },
      {
        fieldApiName: 'type',
        fieldLabel: 'Type',
        fieldType: 'badge',
        isVisible: true,
        order: 2
      },
      {
        fieldApiName: 'format',
        fieldLabel: 'Format',
        fieldType: 'text',
        isVisible: true,
        order: 3
      },
      {
        fieldApiName: 'frequency',
        fieldLabel: 'Frequency',
        fieldType: 'text',
        isVisible: true,
        order: 4
      },
      {
        fieldApiName: 'status',
        fieldLabel: 'Status',
        fieldType: 'badge',
        isVisible: true,
        order: 5
      },
      {
        fieldApiName: 'lastRunDate',
        fieldLabel: 'Last Run',
        fieldType: 'datetime',
        isVisible: true,
        order: 6
      },
      {
        fieldApiName: 'createdBy',
        fieldLabel: 'Created By',
        fieldType: 'text',
        isVisible: true,
        order: 7
      }
    ],
    filters: []
  },
  {
    id: 'lv_reports_sales',
    name: 'Sales Reports',
    apiName: 'sales_reports',
    description: 'Reports related to sales and revenue',
    objectApiName: 'Report',
    isDefault: false,
    columns: [
      {
        fieldApiName: 'name',
        fieldLabel: 'Report Name',
        fieldType: 'text',
        isVisible: true,
        order: 0
      },
      {
        fieldApiName: 'objectName',
        fieldLabel: 'Object',
        fieldType: 'text',
        isVisible: true,
        order: 1
      },
      {
        fieldApiName: 'format',
        fieldLabel: 'Format',
        fieldType: 'text',
        isVisible: true,
        order: 2
      },
      {
        fieldApiName: 'frequency',
        fieldLabel: 'Frequency',
        fieldType: 'text',
        isVisible: true,
        order: 3
      },
      {
        fieldApiName: 'status',
        fieldLabel: 'Status',
        fieldType: 'badge',
        isVisible: true,
        order: 4
      },
      {
        fieldApiName: 'lastRunDate',
        fieldLabel: 'Last Run',
        fieldType: 'datetime',
        isVisible: true,
        order: 5
      },
      {
        fieldApiName: 'nextRunDate',
        fieldLabel: 'Next Run',
        fieldType: 'datetime',
        isVisible: true,
        order: 6
      }
    ],
    filters: [
      {
        fieldApiName: 'type',
        operator: 'equals',
        value: 'Sales'
      }
    ]
  },
  {
    id: 'lv_reports_my',
    name: 'My Reports',
    apiName: 'my_reports',
    description: 'Reports created by me',
    objectApiName: 'Report',
    isDefault: false,
    columns: [
      {
        fieldApiName: 'name',
        fieldLabel: 'Report Name',
        fieldType: 'text',
        isVisible: true,
        order: 0
      },
      {
        fieldApiName: 'type',
        fieldLabel: 'Type',
        fieldType: 'badge',
        isVisible: true,
        order: 1
      },
      {
        fieldApiName: 'status',
        fieldLabel: 'Status',
        fieldType: 'badge',
        isVisible: true,
        order: 2
      },
      {
        fieldApiName: 'lastRunDate',
        fieldLabel: 'Last Run',
        fieldType: 'datetime',
        isVisible: true,
        order: 3
      },
      {
        fieldApiName: 'lastModifiedDate',
        fieldLabel: 'Last Modified',
        fieldType: 'datetime',
        isVisible: true,
        order: 4
      }
    ],
    filters: [
      {
        fieldApiName: 'createdBy',
        operator: 'equals',
        value: 'John Smith' // Mock current user
      }
    ]
  },
  {
    id: 'lv_reports_recent',
    name: 'Recently Run',
    apiName: 'recently_run',
    description: 'Reports run in the last 7 days',
    objectApiName: 'Report',
    isDefault: false,
    columns: [
      {
        fieldApiName: 'name',
        fieldLabel: 'Report Name',
        fieldType: 'text',
        isVisible: true,
        order: 0
      },
      {
        fieldApiName: 'type',
        fieldLabel: 'Type',
        fieldType: 'badge',
        isVisible: true,
        order: 1
      },
      {
        fieldApiName: 'format',
        fieldLabel: 'Format',
        fieldType: 'text',
        isVisible: true,
        order: 2
      },
      {
        fieldApiName: 'lastRunDate',
        fieldLabel: 'Last Run',
        fieldType: 'datetime',
        isVisible: true,
        order: 3
      },
      {
        fieldApiName: 'createdBy',
        fieldLabel: 'Run By',
        fieldType: 'text',
        isVisible: true,
        order: 4
      }
    ],
    filters: []
  }
];

/**
 * Convert Report to ListViewRecord
 */
const reportToListViewRecord = (report: Report): ListViewRecord => {
  return {
    id: report.id,
    name: report.name,
    objectName: report.objectName,
    type: report.type,
    format: report.format,
    frequency: report.frequency,
    status: report.status,
    lastRunDate: report.lastRunDate,
    nextRunDate: report.nextRunDate,
    createdBy: report.createdBy,
    createdDate: report.createdDate,
    lastModifiedDate: report.lastModifiedDate
  };
};

/**
 * Format datetime for display
 */
export const formatDateTime = (isoString: string): string => {
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Hook: Get all report list views
 */
export const useReportListViews = () => {
  return useQuery({
    queryKey: ['reportListViews'],
    queryFn: async () => {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));
      return persistedReportListViews.get();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook: Get default report list view
 */
export const useDefaultReportListView = () => {
  return useQuery({
    queryKey: ['reportListViews', 'default'],
    queryFn: async () => {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 200));
      const allViews = persistedReportListViews.get();
      return allViews.find(view => view.isDefault) || allViews[0];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

const DEFAULT_PAGE_SIZE = 25;

/**
 * Hook: Get report list view data (view definition + filtered records) with server-side pagination
 */
export const useReportListViewData = (
  listViewId: string,
  options?: { page?: number; pageSize?: number }
) => {
  const page = options?.page ?? 0;
  const pageSize = options?.pageSize ?? DEFAULT_PAGE_SIZE;
  const useBackend = shouldUseGobodo() || (USE_RDS_FOR_CORE_OBJECTS && isRdsCoreObject('Report'));

  return useQuery({
    queryKey: ['reportListViewData', listViewId, page, pageSize],
    queryFn: async (): Promise<ListViewData> => {
      const allViews = persistedReportListViews.get();
      const listView = allViews.find(view => view.id === listViewId);
      if (!listView) throw new Error(`List view not found: ${listViewId}`);

      if (useBackend) {
        const { records: pageRecords, totalRecords } = await getRecordsForObjectPage('Report', { page, size: pageSize });
        let records = pageRecords.map(r => normalizeRecordForListView('Report', r) as ListViewRecord);
        if (listView.filters?.length) {
          records = records.filter(record =>
            listView.filters!.every(filter => {
              const value = record[filter.fieldApiName];
              switch (filter.operator) {
                case 'equals': return value === filter.value;
                case 'notEquals': return value !== filter.value;
                case 'contains': return String(value).toLowerCase().includes(String(filter.value).toLowerCase());
                case 'startsWith': return String(value).toLowerCase().startsWith(String(filter.value).toLowerCase());
                default: return true;
              }
            })
          );
        }
        if (listView.id === 'lv_reports_recent') {
          const sevenDaysAgo = new Date('2025-01-10T00:00:00Z');
          records = records.filter(record => {
            if (!record.lastRunDate) return false;
            return new Date(record.lastRunDate as string) >= sevenDaysAgo;
          });
        }
        if (listView.sortBy) {
          records = [...records].sort((a, b) => {
            const aVal = a[listView.sortBy!.fieldApiName];
            const bVal = b[listView.sortBy!.fieldApiName];
            if (listView.sortBy!.direction === 'asc') return aVal > bVal ? 1 : -1;
            return aVal < bVal ? 1 : -1;
          });
        }
        return { listView, records, totalRecords };
      }

      await new Promise(resolve => setTimeout(resolve, 400));
      const allRecords = persistedReportRecords.get();
      let filtered = [...allRecords];
      if (listView.filters?.length) {
        filtered = filtered.filter(record =>
          listView.filters!.every(filter => {
            const value = record[filter.fieldApiName];
            switch (filter.operator) {
              case 'equals': return value === filter.value;
              case 'notEquals': return value !== filter.value;
              case 'contains': return String(value).toLowerCase().includes(String(filter.value).toLowerCase());
              case 'startsWith': return String(value).toLowerCase().startsWith(String(filter.value).toLowerCase());
              default: return true;
            }
          })
        );
      }
      if (listView.id === 'lv_reports_recent') {
        const sevenDaysAgo = new Date('2025-01-10T00:00:00Z');
        filtered = filtered.filter(record => {
          if (!record.lastRunDate) return false;
          return new Date(record.lastRunDate as string) >= sevenDaysAgo;
        });
      }
      if (listView.sortBy) {
        filtered = [...filtered].sort((a, b) => {
          const aVal = a[listView.sortBy!.fieldApiName];
          const bVal = b[listView.sortBy!.fieldApiName];
          if (listView.sortBy!.direction === 'asc') return aVal > bVal ? 1 : -1;
          return aVal < bVal ? 1 : -1;
        });
      }
      const totalRecords = filtered.length;
      const start = page * pageSize;
      const records = filtered.slice(start, start + pageSize);
      return { listView, records, totalRecords };
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!listViewId,
    placeholderData: keepPreviousData,
  });
};

/**
 * Update a report list view
 * Used by EditViewModal to persist changes
 */
export function useUpdateReportListView() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<ListView> }) => {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));

      // Update in localStorage via mockDataManager
      const updated = persistedReportListViews.update(id, {
        ...updates,
        lastModifiedDate: new Date().toISOString()
      });

      if (!updated) {
        throw new Error('List view not found');
      }

      return updated;
    },
    onSuccess: (data) => {
      // Invalidate queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['reportListViews'] });
      queryClient.invalidateQueries({ queryKey: ['reportListViewData', data.id] });
    },
  });
}
